import React from "react";
import {useTranslation} from "react-i18next";
import {Grid, ListItem, ListItemIcon, ListItemText, Paper} from "@material-ui/core";
import List from "@material-ui/core/List";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {IShipmentSummary} from "../../pages/SingleShipmentTrackingPage";
import Loader from "../core/Loader";
import {InfoOutlined} from "@material-ui/icons";
import withWidth, {WithWidthProps} from "@material-ui/core/withWidth";
import {getShipmentSummary} from "../../api/ShipmentEndpoint";
import {RR_STATUS} from "../../constants/RRConstants";
import {useSnackbar} from "notistack";
import {InfoCardLine} from "../page-single-shipment-tracking/ShipmentInfoCard";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
        },
        lineItemContextContainer: {
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            alignItems: "center"
        },
        shipmentInfoContainer: {
            display: "flex",
            alignItems: "center",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        shipmentInfoCard: {
            flex: 1
        },
        caption: {
            fontWeight: "bold",
            marginRight: 5
        },
        value: {
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: '11rem'
        }
    }),
);


interface IShipmentInfoCard {
    uuid: string
}


const ShipmentDetailsCard: React.FC<IShipmentInfoCard & WithWidthProps> = ({uuid, width}) => {
    const classes = useStyles();

    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const [shipmentSummary, setShipmentSummary] = React.useState<IShipmentSummary | undefined>(undefined);

    React.useEffect(() => {

        getShipmentSummary(uuid).then(({data}) => {
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setShipmentSummary(response);
            }
        });


    }, [enqueueSnackbar, uuid, t]);

    if (!shipmentSummary) {
        return <Loader/>;
    }

    return <Paper className={classes.root}>
        <List>
            <ListItem>
                <ListItemIcon>
                    <InfoOutlined fontSize="large"/>
                </ListItemIcon>
                <ListItemText
                    primary={<CardItem shipmentSummary={shipmentSummary} width={width}/>}
                />
            </ListItem>

        </List>
    </Paper>
};


const CardItem: React.FC<{ shipmentSummary: IShipmentSummary } & WithWidthProps> = ({shipmentSummary, width}) => {

    const {t} = useTranslation();

    if (!shipmentSummary) {
        return <Loader/>;
    }

    const {departure, deliveryTerm, arrival, reference, orderDate} = shipmentSummary;

    const date = orderDate ? /\d{4}-\d{2}-\d{2}/.exec(orderDate) : "";


    return <React.Fragment>

        <Grid container justifyContent="center"
              alignItems="center">
            <Grid item xs={12} sm={6}>
                <InfoCardLine property={t("shipment.departure")} value={departure} width={width}/>
                <InfoCardLine property={t("shipment.deliveryTerm")} value={deliveryTerm} width={width}/>
                <InfoCardLine property={t("shipment.arrival")} value={arrival} width={width}/>
            </Grid>

            <Grid item xs={12} sm={6}>
                <InfoCardLine property={t("shipment.reference")} value={reference} width={width}/>
                <InfoCardLine property={t("shipment.orderDate")} value={date} width={width}/>
            </Grid>

        </Grid>

    </React.Fragment>
};


export default withWidth()(ShipmentDetailsCard);
